/** @format */

import {Box, IconButton} from '@mui/material';

import {KeyboardArrowDown, KeyboardArrowUp} from '@mui/icons-material';

import {flexRender, Row} from '@tanstack/react-table';

import {useTheme} from '@mui/material/styles';

import {StyledTableRow} from './styles';
import {
  ARROW_CELL_WIDTH,
  CELL_HEIGHT,
  CHECKBOX_CELL_WIDTH,
  CHECKBOX_OFFSET_X,
} from '../../constants';
import {ConstantsList, TanStackTableData, TableVariant} from '../../types';
import {TableCellMemoized} from '../TableCellMemoized';
import {StyledCheckbox} from '../../styles';

interface Props {
  isWithSubRows: boolean;
  rows: Row<TanStackTableData>[];
  ['data-index']: number;
  enableRowSelection?: boolean;
  isRowSelectionDisabled?: (row: Row<TanStackTableData>) => boolean;
  constants: ConstantsList;
  variant: TableVariant;
}

const TableBodyRow = ({
  isWithSubRows,
  rows,
  enableRowSelection,
  isRowSelectionDisabled,
  constants,
  variant,
  ...props
}: Props) => {
  const theme = useTheme();
  const iconColor =
    variant === 'light'
      ? theme.palette.figma_gray_dark_5?.main
      : theme.palette.figma_gray_light_1?.main;

  const index = props['data-index'];
  const row = rows[index];
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();
  const onRowClick = row.original.onRowClick;
  const toggleExpanded = row.getToggleExpandedHandler();

  const isSelectionDisabled = isRowSelectionDisabled?.(row) ?? false;

  const handleClick = () => {
    if (canExpand) {
      toggleExpanded();
    }
    if (enableRowSelection && !isSelectionDisabled) {
      row.toggleSelected();
    }
    onRowClick?.();
  };

  const canClick = Boolean(onRowClick);

  const {
    arrowCellWidth = ARROW_CELL_WIDTH,
    cellHeight = CELL_HEIGHT,
    checkboxCellWidth = CHECKBOX_CELL_WIDTH,
    checkboxOffsetX = CHECKBOX_OFFSET_X,
  } = constants;

  const visibleCells = row.getVisibleCells();

  return (
    <StyledTableRow
      {...props}
      key={row.id}
      canExpand={canExpand}
      canClick={canClick}
      canSelect={Boolean(enableRowSelection) && !isSelectionDisabled}
      onClick={handleClick}
      variant={variant}
    >
      {isWithSubRows && (
        <TableCellMemoized sx={{padding: 0}} cellHeight={cellHeight} tableVariant={variant}>
          {canExpand && (
            <IconButton
              onClick={e => {
                e.stopPropagation();
                handleClick();
              }}
              sx={{
                width: `${arrowCellWidth}px`,
                height: `${cellHeight - 2}px`,
              }}
              data-testid={`row-button-${row.index}`}
            >
              {isExpanded ? (
                <KeyboardArrowUp sx={{color: iconColor}} />
              ) : (
                <KeyboardArrowDown sx={{color: iconColor}} />
              )}
            </IconButton>
          )}
        </TableCellMemoized>
      )}

      {enableRowSelection && (
        <TableCellMemoized
          width={checkboxCellWidth}
          cellHeight={cellHeight}
          tableVariant={variant}
          sx={{
            padding: theme.spacing(1, 0.5),
          }}
        >
          {/* stopPropagation prevents double-toggle when clicking checkbox directly */}
          <Box onClick={e => e.stopPropagation()}>
            <StyledCheckbox
              checked={row.getIsSelected()}
              disabled={isSelectionDisabled}
              onChange={row.getToggleSelectedHandler()}
              checkboxOffsetX={checkboxOffsetX}
            />
          </Box>
        </TableCellMemoized>
      )}

      {visibleCells.map((cell, index) => {
        const shouldEnableHover = cell.column.columnDef.meta?.enableHoverExpand;
        const rowIsExpandable = row.original.isExpandable;

        // Логика коллапса подстрок в одну ячейку
        let isCollapseSubRows: boolean | undefined;
        let dontRender: boolean | undefined;
        const isSubRow = cell.row.depth > 0;
        if (isSubRow) {
          const parentRow = row.getParentRow();
          if (parentRow) {
            isCollapseSubRows = index === 0 && parentRow.original.isCollapseSubRows;
            dontRender = index > 0 && parentRow.original.isCollapseSubRows;
          }
        }
        if (dontRender) return null;

        return (
          <TableCellMemoized
            key={cell.id}
            cellHeight={cellHeight}
            enableHoverExpand={shouldEnableHover}
            isExpandable={rowIsExpandable}
            isWithSubRows={isWithSubRows}
            tableVariant={variant}
            colSpan={isCollapseSubRows ? visibleCells.length : undefined}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCellMemoized>
        );
      })}
    </StyledTableRow>
  );
};

export {TableBodyRow};
